/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, Fragment, useContext } from 'react';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FlowVersion,
  FlowFeedback,
  FlowComment,
  FlowSPRData,
  FlowDocument,
} from '../../../../services/types/Flow';
import { Button, Modal, LinkButton } from '../../../../shared/components';
import {
  customDateStr,
  dateTimeFormat,
  safeFind,
  tooltipPreventOverflowModifiers,
} from '../../../../shared/misc/utils';
import FlowStatusBadge from './FlowStatusBadge';
import CommentsTable from './Comments/CommentsTable';
import PerformanceBadge from '../../PerformanceBadge';
import EsrrBadge from '../../EsrrBadge';
import SprTable from './Spr/SprTable';
import DocumentsTable from './Documents/DocumentsTable';
import OperationContext from '../context';

interface Props {
  versionsData: FlowVersion[];
  openWord?: () => void;
  handleDownloadDoc: (versionId: number) => void;
  flowType: string;
  flowTypeName: string;
  commentsCount: number;
  flowId: number;
  downloadEsap: (versionId: number) => void;
}

interface IndicationState {
  feedbacks: FlowFeedback[];
  version: number;
  lap: number;
}

const FlowVersionsTable: React.FC<Props> = (props) => {
  const {
    versionsData,
    openWord,
    handleDownloadDoc,
    flowType,
    flowTypeName,
    commentsCount,
    flowId,
    downloadEsap,
  } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { operation } = useContext(OperationContext);
  const [showFeedbacks, setShowFeedbacks] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [indicationsTableState, setIndicationsTableState] = useState<IndicationState[] | null>(
    null
  );
  const [selectedComment, setSelectedComment] = useState<FlowComment | null>(null);
  const [showRationaleModal, setShowRationaleModal] = useState(false);
  const [showSupportDocumentModal, setShowSupportDocumentModal] = useState(false);

  const [sprData, setSprData] = useState<FlowSPRData | null>(null);
  const [historicSupportDocumentsData, setHistoricSupportDocumentsData] = useState<{
    data: FlowDocument[];
    lap: number;
  } | null>(null);

  const tableClass = 'history-content flow-history table-responsive';
  const [expandedLaps, setExpandedLaps] = useState<{ lap: number; flowId: number }[]>([]);

  const currentVersion = versionsData[0];
  currentVersion.comments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const previousVersions = versionsData.filter((x) => x.id !== currentVersion.id);
  const orderedFlows = [...previousVersions].sort(
    (a, b) => new Date(b.modifiedOn).getTime() - new Date(a.modifiedOn).getTime()
  );
  const flows = orderedFlows;

  const downloadEzshare = (ezShareCode: string) => {
    window.open(`https://idbdocs.iadb.org/wsdocs/getdocument.aspx?docnum=${ezShareCode}`, '_blank');
  };

  const closeFeedbacksModal = () => {
    setIndicationsTableState(null);
    setShowFeedbacks(false);
  };
  const closeCommentsModal = () => {
    setSelectedComment(null);
    setShowComment(false);
  };

  const showRationale = (data: FlowSPRData | null) => {
    setSprData(data);
    setShowRationaleModal(true);
  };

  const showHistoricSupportDocuments = (data: FlowDocument[], lap: number) => {
    setHistoricSupportDocumentsData({ data, lap });
    setShowSupportDocumentModal(true);
  };

  const showFeedbacksModal = (id: number) => {
    const current = safeFind(versionsData, (x) => x.id === id);
    setIndicationsTableState([
      { feedbacks: current.feedbacks, version: current.version, lap: current.lap },
    ]);
    setShowFeedbacks(true);
  };

  const validSpr = (spr: FlowSPRData) =>
    spr.startDate != null && customDateStr(spr.startDate.toString()) !== '01/01/0001';

  const showLapFeedbacksModal = (lap: number) => {
    const lapVersions = versionsData.filter((v) => v.lap === lap);

    const indications = lapVersions.map(
      (v) => ({ feedbacks: v.feedbacks, version: v.version, lap } as IndicationState)
    );

    setIndicationsTableState(indications);
    setShowFeedbacks(true);
  };

  const showCommentModal = (comment: FlowComment) => {
    setSelectedComment(comment);
    setShowComment(true);
  };

  const isSR = flowType === 'SR' || flowType === 'SR_ESPF';

  const renderIndications = () => {
    if (!indicationsTableState) {
      return null;
    }
    return indicationsTableState.map((i, indexI) =>
      i.feedbacks.map((f, indexF) => (
        <tr key={`feedbackTable_tr${indexI.toString()}_${indexF.toString()}`}>
          <td className="text-nowrap">{`${flowTypeName} v.${i.lap}.${i.version}`}</td>
          <td className="text-nowrap">{f.user}</td>

          <td>{dateTimeFormat(f.date)}</td>
          <td>{t(`flowIndications.${f.indicationCode}`)}</td>
          <td className="S3">{f.comment || <span>Without comments</span>}</td>
        </tr>
      ))
    );
  };

  const versionTr = (x: FlowVersion, i: number, comment: FlowComment | undefined = undefined) => {
    const isLegacy = x.source === 'Legacy_Convergence';
    const versionText = isLegacy
      ? `${flowTypeName} v.${'0'}`
      : `${flowTypeName} v.${x.lap.toString()}.${x.version.toString()}`;
    return (
      (x.status === 'APPROVED' ||
        (x.lap === currentVersion.lap && x.flowId === flowId) ||
        expandedLaps.some((el) => el.lap === x.lap && el.flowId === x.flowId)) && (
        <tr key={`tr-version-${i.toString()}`}>
          <td style={{ maxWidth: 50 }} colSpan={1}>
            {x.status === 'APPROVED' && !isLegacy ? (
              <Button
                className="btn-tertiary pl-1"
                iconRight={
                  expandedLaps.some((el) => el.lap === x.lap && el.flowId === x.flowId)
                    ? 'chevron-up'
                    : 'chevron-down'
                }
                text=""
                type="button"
                onClick={() => {
                  const index = expandedLaps.findIndex(
                    (el) => el.lap === x.lap && el.flowId === x.flowId
                  );
                  if (index === -1) {
                    setExpandedLaps([...expandedLaps, { lap: x.lap, flowId: x.flowId }]);
                  } else {
                    setExpandedLaps(
                      expandedLaps.filter((el) => !(el.lap === x.lap && el.flowId === x.flowId))
                    );
                  }
                }}
              />
            ) : (
              ''
            )}
          </td>

          <td>
            {openWord ? (
              <LinkButton onClick={openWord} icon="file-word" text={versionText} />
            ) : (
              <div className="d-flex">
                {versionText}
                {isLegacy && (
                  <span className="deleted-toolkit">
                    <div className="ml-2 toolkit-tooltip" id={`tr-toolkit-id-${x.id}`}>
                      <FontAwesomeIcon icon="exclamation-circle" className="lastUpdateInfo" />
                    </div>
                    <UncontrolledTooltip
                      className="tooltip-toolkit lastUpdateInfo"
                      placement="bottom"
                      target={`tr-toolkit-id-${x.id}`}
                      modifiers={tooltipPreventOverflowModifiers}
                    >
                      {t('sprMigratedMsg')}
                    </UncontrolledTooltip>
                  </span>
                )}

                {operation.number !== x.operationNumber && (
                  <>
                    <div className="ml-2" id={`tr-version-${i.toString()}-warning`}>
                      <FontAwesomeIcon color="#fab428" icon="exclamation-triangle" />
                    </div>
                    <UncontrolledTooltip
                      className="tooltip-impact"
                      placement="bottom"
                      target={`tr-version-${i.toString()}-warning`}
                      modifiers={tooltipPreventOverflowModifiers}
                    >
                      {t('warningRelatedFlowVersion', { operationNumber: x.operationNumber })}
                    </UncontrolledTooltip>
                  </>
                )}
                {operation.number === x.operationNumber &&
                  x.relatedsOperationNumber !== null &&
                  x.relatedsOperationNumber.length > 0 && (
                    <>
                      <div className="ml-2" id={`tr-version-${i.toString()}-warning`}>
                        <FontAwesomeIcon color="#fab428" icon="exclamation-triangle" />
                      </div>
                      <UncontrolledTooltip
                        className="tooltip-impact"
                        placement="bottom"
                        target={`tr-version-${i.toString()}-warning`}
                        modifiers={tooltipPreventOverflowModifiers}
                      >
                        {t('warningRelatedParentFlowVersion', {
                          operationNumber: x.relatedsOperationNumber,
                        })}
                      </UncontrolledTooltip>
                    </>
                  )}
              </div>
            )}
          </td>
          <td>
            <FlowStatusBadge code={x.status} />
          </td>
          {isSR && (
            <>
              <td>
                {x.spr && x.status === 'APPROVED' && (
                  <PerformanceBadge code={x.spr.rating.substring(1)} />
                )}
              </td>
              <td>
                {x.spr && x.spr.esrrRating && x.status === 'APPROVED' && (
                  <EsrrBadge text={x.spr.esrrRating} />
                )}
              </td>
            </>
          )}
          <td>{comment ? dateTimeFormat(comment.date) : dateTimeFormat(x.modifiedOn)}</td>
          {isSR && (
            <td>
              {x.status === 'APPROVED' &&
                x.spr != null &&
                x.spr.startDate &&
                x.spr.endDate &&
                validSpr(x.spr) &&
                `${customDateStr(x.spr.startDate.toString())} - ${customDateStr(
                  x.spr.endDate.toString()
                )}`}
            </td>
          )}
          <td>{comment ? comment.author : x.modifiedBy}</td>
          {isSR ? (
            <>
              <td>
                {x.status === 'APPROVED' && !isLegacy ? (
                  <>
                    <Button
                      htmlId={`feedback_button_${x.id}`}
                      className="btn-tertiary no-padding"
                      icon="exchange-alt"
                      text=""
                      onClick={() => showLapFeedbacksModal(x.lap)}
                    />
                    <UncontrolledTooltip
                      className="tooltip-impact"
                      target={`feedback_button_${x.id}`}
                      modifiers={tooltipPreventOverflowModifiers}
                    >
                      <p>Reviewer Feedbacks</p>
                    </UncontrolledTooltip>
                  </>
                ) : (
                  x.feedbacks &&
                  x.feedbacks.length > 0 && (
                    <>
                      <Button
                        htmlId={`feedback_button_${x.id}`}
                        className="btn-tertiary no-padding"
                        icon="exchange-alt"
                        text=""
                        onClick={() => showFeedbacksModal(x.id)}
                      />
                      <UncontrolledTooltip
                        className="tooltip-impact"
                        target={`feedback_button_${x.id}`}
                        modifiers={tooltipPreventOverflowModifiers}
                      >
                        <p>Reviewer Feedback</p>
                      </UncontrolledTooltip>
                    </>
                  )
                )}
              </td>
              <td>
                {x.status === 'APPROVED' &&
                  flowType === 'SR_ESPF' &&
                  (x.esap ? (
                    <>
                      <Button
                        htmlId={`EsapDownloadLap${x.esap.srEspfLap}`}
                        className="btn-tertiary no-padding"
                        text={`V.${x.esap.version.toString()}`}
                        onClick={() => x.esap && downloadEsap(x.esap.id)}
                      />
                      <UncontrolledTooltip
                        className="tooltip-impact"
                        target={`EsapDownloadLap${x.esap.srEspfLap}`}
                        modifiers={tooltipPreventOverflowModifiers}
                      >
                        <p>{t('downloadEsap')}</p>
                      </UncontrolledTooltip>
                    </>
                  ) : (
                    t('noEsap')
                  ))}
                {x.status === 'APPROVED' && x.flowDocuments && x.flowDocuments.length > 0 && (
                  <>
                    <Button
                      htmlId={`documents_button_${x.id}`}
                      className="btn-tertiary no-padding"
                      icon="file"
                      text=""
                      onClick={() => showHistoricSupportDocuments(x.flowDocuments, x.lap)}
                    />
                    <UncontrolledTooltip
                      className="tooltip-impact"
                      target={`documents_button_${x.id}`}
                      modifiers={tooltipPreventOverflowModifiers}
                    >
                      <p>Support Documents</p>
                    </UncontrolledTooltip>
                  </>
                )}
              </td>
              <td>
                {x.status === 'APPROVED' && (
                  <>
                    <Button
                      htmlId={`spr_button_${x.id}`}
                      className="btn-tertiary no-padding"
                      icon="clipboard-list"
                      text=""
                      onClick={() => showRationale(x.spr)}
                    />
                    <UncontrolledTooltip
                      className="tooltip-impact"
                      target={`spr_button_${x.id}`}
                      modifiers={tooltipPreventOverflowModifiers}
                    >
                      <p>SPR Rationale</p>
                    </UncontrolledTooltip>
                  </>
                )}
              </td>
              <td>
                <Button
                  htmlId={`download_button_${x.id}`}
                  className="btn-tertiary no-padding"
                  icon="file-download"
                  text=""
                  onClick={() =>
                    isLegacy ? downloadEzshare(x.ezShareDocumentCode) : handleDownloadDoc(x.id)
                  }
                />
                <UncontrolledTooltip
                  className="tooltip-impact"
                  target={`download_button_${x.id}`}
                  modifiers={tooltipPreventOverflowModifiers}
                >
                  <p>Download</p>
                </UncontrolledTooltip>
              </td>
            </>
          ) : (
            <>
              <td>
                {comment ? (
                  <Button
                    className="btn-tertiary no-padding"
                    icon="comment"
                    text={t('comment')}
                    onClick={() => showCommentModal(comment)}
                  />
                ) : (
                  x.feedbacks &&
                  x.feedbacks.length > 0 && (
                    <Button
                      className="btn-tertiary no-padding"
                      icon="file-alt"
                      text={t('feedback')}
                      onClick={() => showFeedbacksModal(x.id)}
                    />
                  )
                )}
              </td>
              <td>
                {x.status === 'APPROVED' &&
                  (flowType === 'SR_ESPF' || flowType === 'ESRS') &&
                  (x.esap ? (
                    <>
                      <Button
                        htmlId={`EsapDownloadLap${x.esap.esrsLap}`}
                        className="btn-tertiary no-padding"
                        text={`V.${x.esap.version.toString()}`}
                        onClick={() => x.esap && downloadEsap(x.esap.id)}
                      />
                      <UncontrolledTooltip
                        className="tooltip-impact"
                        target={`EsapDownloadLap${x.esap.esrsLap}`}
                        modifiers={tooltipPreventOverflowModifiers}
                      >
                        <p>{t('downloadEsap')}</p>
                      </UncontrolledTooltip>
                    </>
                  ) : (
                    t('noEsap')
                  ))}
              </td>
              <td>
                <Button
                  htmlId={`esrsDownload${x.id}`}
                  className="btn-tertiary no-padding"
                  icon="file-download"
                  text=""
                  onClick={() => handleDownloadDoc(x.id)}
                />
                {flowType === 'ESRS' && (
                  <UncontrolledTooltip
                    className="tooltip-impact"
                    placement="bottom"
                    target={`esrsDownload${x.id}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('downloadESRS')}
                  </UncontrolledTooltip>
                )}
              </td>
            </>
          )}
        </tr>
      )
    );
  };

  const renderComments = (version: FlowVersion) =>
    version.comments.map((comment, i) => (
      <Fragment key={`rowComment_${version.id}_${i}`}>{versionTr(version, i, comment)}</Fragment>
    ));

  const renderVersions = () =>
    flows.map((x, i) => {
      if (x.comments.length > 0) {
        x.comments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        const lastCommentTime = new Date(x.comments[0].date).getTime();
        const versionTime = new Date(x.modifiedOn).getTime();
        if (lastCommentTime > versionTime) {
          return (
            <Fragment key={`feedbackTable_td_${x.id}_${i}`}>
              {renderComments(x)}
              {x.feedbacks.length > 0 && versionTr(x, i)}
            </Fragment>
          );
        }
        return (
          <Fragment key={`feedbackTable_td_${x.id}_${i}`}>
            {x.feedbacks.length > 0 && versionTr(x, i)}
            {renderComments(x)}
          </Fragment>
        );
      }

      return <Fragment key={`feedbackTable_td_${x.id}_${i}`}>{versionTr(x, i)}</Fragment>;
    });

  const GoToComments = () => {
    navigate(`${location.pathname}/comments`);
  };

  return (
    <>
      <div className={tableClass}>
        <Table className="flow-table">
          <thead>
            <tr>
              <th> </th>
              <th>{t('version')}</th>
              <th>{t('status')}</th>
              {isSR && (
                <>
                  <th>{t('spr')}</th>
                  <th>{t('esrr')}</th>
                </>
              )}
              <th>{t('lastModification')}</th>
              {isSR && <th>{t('supervisionDates')}</th>}
              <th>{t('modifiedByWithComments')}</th>
              {isSR && flowType !== 'SR_ESPF' ? (
                <th colSpan={4} style={{ width: '85px' }}>
                  {t('actions')}
                </th>
              ) : (
                <>
                  <th>
                    {`${t('comments')} /`}
                    <br />
                    {`${t('feedbacks')}`}
                  </th>
                  {(flowType === 'SR_ESPF' || flowType === 'ESRS') && (
                    <th style={{ maxWidth: 90 }} colSpan={1}>
                      {t('esap')}
                    </th>
                  )}
                  <th style={{ maxWidth: 90 }} colSpan={2}>
                    {`${t('document')} /`}
                    <br />
                    {`${t('download')}`}
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {currentVersion.comments.length > 0 && renderComments(currentVersion)}
            {renderVersions()}
          </tbody>
        </Table>
      </div>

      <Modal
        title={t('feedbacks')}
        visible={showFeedbacks}
        setVisible={() => setShowFeedbacks(false)}
        actions={[{ text: 'Close', action: closeFeedbacksModal }]}
        customClass="superextra_wide-modal"
        onClosed={closeFeedbacksModal}
      >
        <div className="history-content flow-history mb-0 table-responsive">
          <Table responsive>
            <thead>
              <tr>
                <th>{t('version')}</th>
                <th>{t('modifiedBy')}</th>
                <th>{t('date')}</th>
                <th>{t('indication')}</th>
                <th>{t('comments')}</th>
              </tr>
            </thead>
            <tbody>{renderIndications()}</tbody>
          </Table>
        </div>
      </Modal>

      <Modal
        title={t('rationale')}
        visible={showRationaleModal}
        setVisible={() => setShowRationaleModal(false)}
        actions={[{ text: 'Close', action: () => setShowRationaleModal(false) }]}
        customClass="extra_wide-modal"
        onClosed={() => setShowRationaleModal(false)}
      >
        <SprTable data={sprData} />
      </Modal>

      {historicSupportDocumentsData && (
        <Modal
          title={t('supportDocuments')}
          visible={showSupportDocumentModal}
          setVisible={() => setShowSupportDocumentModal(false)}
          actions={[{ text: 'Close', action: () => setShowSupportDocumentModal(false) }]}
          customClass="extra_wide-modal"
          onClosed={() => setShowSupportDocumentModal(false)}
        >
          <DocumentsTable
            data={historicSupportDocumentsData.data}
            flowId={flowId}
            flowLap={historicSupportDocumentsData.lap}
          />
        </Modal>
      )}

      <Modal
        title={t('comments')}
        visible={showComment}
        setVisible={() => setShowComment(false)}
        actions={[
          {
            text: `Show All Comments (${commentsCount})`,
            icon: 'comment',
            action: GoToComments,
            className: 'btn-tertiary',
          },
          { text: 'Close', action: closeCommentsModal, className: 'btn-tertiary' },
        ]}
        customClass="extra_wide-modal"
        onClosed={closeCommentsModal}
      >
        <div className="history-content flow-history mb-0">
          {selectedComment && (
            <CommentsTable flowType={flowTypeName} commentListData={[selectedComment]} />
          )}
        </div>
      </Modal>
    </>
  );
};

export default FlowVersionsTable;
